// #3652 -- Settings "Rings and days": one row per ring, one column per venue
// day, each cell a count linking to the Classes page filtered to that ring and
// day. Replaces foxbox's run-on ringDayStructure string.
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { unwrap } from "../api/scoreApi";
import { formatDayKey } from "../util/classDisplay";
import { buildRingDayTable } from "../util/ringDayTable";

// "Wed, Oct 21" -> "Wed Oct 21" (the approved mockup's column header).
const dayHeader = (key) => formatDayKey(key).replace(",", "");

function ProblemLine({ text, linkLabel, testId, to }) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", minWidth: 0 }} data-testid={testId}>
      <Typography
        variant="body2"
        noWrap
        sx={{ color: "warning.dark", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        data-testid={`${testId}-text`}
      >
        {text}
      </Typography>
      <Link component={RouterLink} to={to} variant="body2" sx={{ flexShrink: 0 }}>
        {linkLabel}
      </Link>
    </Box>
  );
}

export default function RingsAndDaysSection({ showId, scoreApi }) {
  const { t } = useTranslation("scoreadmin");
  const [classes, setClasses] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setClasses(null);
    setFailed(false);
    (async () => {
      try {
        const list = unwrap(await scoreApi.classes.listClasses(showId));
        if (!cancelled) setClasses(list);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showId, scoreApi]);

  const table = useMemo(() => (classes ? buildRingDayTable(classes) : null), [classes]);
  const openClasses = t("settings.ringsAndDays.openClasses");

  return (
    <Paper sx={{ p: 2 }} data-testid="settings-structure">
      <Typography variant="h6" gutterBottom color="text.primary">
        {t("settings.sections.structure")}
      </Typography>
      {failed && (
        <Typography variant="body2" color="text.secondary" data-testid="rings-days-error">
          {t("settings.ringsAndDays.loadFailed")}
        </Typography>
      )}
      {table && table.rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" data-testid="rings-days-empty">
          {t("settings.ringsAndDays.empty")}
        </Typography>
      )}
      {table && table.rows.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" data-testid="rings-days-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("settings.ringsAndDays.ring")}</TableCell>
                {table.days.map((d) => (
                  <TableCell key={d} align="right" data-testid={`rings-days-head-${d}`}>
                    {dayHeader(d)}
                  </TableCell>
                ))}
                <TableCell align="right">{t("settings.ringsAndDays.total")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map((row) => (
                <TableRow key={row.ring} data-testid="rings-days-row">
                  <TableCell component="th" scope="row">
                    {row.ring}
                  </TableCell>
                  {table.days.map((d) => {
                    const n = row.counts[d] ?? 0;
                    return (
                      <TableCell key={d} align="right" data-testid={`rings-days-cell-${d}`}>
                        {n > 0 ? (
                          <Link
                            component={RouterLink}
                            to={`/classes?${new URLSearchParams({ ring: row.ring, day: d })}`}
                          >
                            {t("settings.ringsAndDays.classCount", { count: n })}
                          </Link>
                        ) : (
                          "–"
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" data-testid="rings-days-total">
                    {row.total}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {table && (table.noRing > 0 || table.noDay > 0) && (
        <Box sx={{ mt: 1.5, minWidth: 0 }}>
          {table.noRing > 0 && (
            <ProblemLine
              testId="rings-days-problem-no-ring"
              to="/classes?problem=noRing"
              text={t("settings.ringsAndDays.noRing", { count: table.noRing })}
              linkLabel={openClasses}
            />
          )}
          {table.noDay > 0 && (
            <ProblemLine
              testId="rings-days-problem-no-day"
              to="/classes?problem=noDay"
              text={t("settings.ringsAndDays.noDay", { count: table.noDay })}
              linkLabel={openClasses}
            />
          )}
        </Box>
      )}
    </Paper>
  );
}
